import type { SimplifiedSeries } from "@similarweb/pro-logic-components";
import { colorsPalettes, colorsSets } from "@similarweb/styles";

import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import type { ISearchAdsSpend } from "app/services/searchAdsSpend/types";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { ISearchTrafficData, ISearchTrafficDataCompare, SeriesLineItem } from "./types";

const CHANNEL_KEY_DESKTOP = "Paid Search";
const CHANNEL_KEY_TOTAL = "Search - Paid";
const { blue } = colorsPalettes;

/** Spend months arrive as full timestamps while traffic months are plain dates; join on the date part. */
const toMonthKey = (date: string): string => date.slice(0, 10);

const indexTrafficByMonth = (
    breakDown: Record<string, Record<string, number>> | undefined,
): Map<string, Record<string, number>> => {
    const byMonth = new Map<string, Record<string, number>>();
    for (const [date, channels] of Object.entries(breakDown ?? {})) {
        byMonth.set(toMonthKey(date), channels);
    }
    return byMonth;
};

/**
 * transformCostPerVisitData:
 * Combines PPC spend data and search traffic data into a cost-per-visit chart series.
 *
 * @param spend - spend per domain and month, already converted into the selected currency
 * @param searchTrafficData - The raw data from useSearchTrafficData (traffic per channel/date), either compare or non-compare shape
 * @param isCompare - if true, use the compare interface shape. If false, use the non-compare shape
 */
export function transformCostPerVisitData(
    spend: ISearchAdsSpend | undefined,
    searchTrafficData: ISearchTrafficData | ISearchTrafficDataCompare | undefined,
    isCompare?: boolean,
    webSource?: IWidgetModelTypesWebSource,
): SimplifiedSeries<SeriesLineItem> {
    if (!spend || !searchTrafficData) {
        return [];
    }

    const channelKey = webSource === devicesTypes.TOTAL ? CHANNEL_KEY_TOTAL : CHANNEL_KEY_DESKTOP;

    const trafficBreakDown = isCompare
        ? (searchTrafficData as ISearchTrafficDataCompare)?.TrafficShare?.[channelKey]?.BreakDown
        : (searchTrafficData as ISearchTrafficData)?.TrafficShare?.Data?.BreakDown;

    const trafficByMonth = indexTrafficByMonth(trafficBreakDown);

    const colorsSet = colorsSets.c.toArray();

    return spend.series.map((domainSeries, index) => {
        const data = domainSeries.points.map((point) => {
            const traffic = trafficByMonth.get(toMonthKey(point.date))?.[isCompare ? domainSeries.domain : channelKey];

            const hasCostPerVisit = point.value !== null && traffic > 0;

            return {
                x: new Date(point.date).getTime(),
                y: hasCostPerVisit ? point.value / traffic : null,
            };
        });

        const valued = data.filter((item) => item.y !== null);
        const averageY = valued.length > 0 ? valued.reduce((sum, item) => sum + item.y, 0) / valued.length : 0;

        return {
            id: domainSeries.domain,
            name: domainSeries.domain,
            data,
            visible: true,
            color: isCompare ? colorsSet[index % colorsSet.length] : blue["400"],
            total: averageY,
        };
    });
}
